import { pickBy } from 'lodash-es';
import { stringify } from 'querystring';

import { getPageVariable } from '@bucketplace/bucket-log';
import { getQuery } from '@bucketplace/lib';
import { omit } from '@bucketplace/lib';
import { UTM_TAGS, QUERY_BLACKLIST, escapeBracesInQuery, getUtmData, GLOBAL_DEEP_LINK_HOST, DEEP_LINK_HOST, isAllowedGlobalLocale, getGlobalLanguageFromLocaleString } from '@bucketplace/common';

interface GetDeepLinkParams {
  /** 추가로 더 포함할 params */
  additionalParams?: { [key: string]: string };
  /** pathname */
  pathname?: string;
  /** query  */
  query?: string;
  /** host 호스트
   * protocol을 제외하고 전달
   * default: undefined */
  host?: string;
  /** fallback url */
  fallbackDesktopUrl?: string;
  /** owap 유무 */
  isOwap?: boolean;
  /** global 유무 */
  isGlobal?: boolean;
  /** owap webview type */
  owapWebviewType?: 'page' | 'modal' | 'fullmodal' | 'fullpage';
}

/**
 * NOTE This relies on document / window object. Therefore, SSR is not possible
 * with this - it's recommend to use location.href = getDeepLink() or
 * equivalent to defer retriving UTM data.
 * query에서 블랙리스트를 제외하고 utm tags 기반으로 deeplink를 생성
 * @param additionalParams 추가로 더 포함할 params
 * @param pathname pathname
 * @param query query
 * @param host 각 호스트 store.ohou.se, contents.ohou.se, o2o.ohou.se
 * @param fallbackDesktopUrl fallback url
 * @param isOwap owap 유무
 * @param isGlobal global 유무
 * @param owapWebviewType owap webview type
 */
export function getDeepLink({
  additionalParams,
  pathname,
  query,
  host,
  fallbackDesktopUrl,
  isOwap,
  isGlobal,
  owapWebviewType,
}: GetDeepLinkParams = {}): string {
  const queryObj = getQuery(query ?? location.search);
  const essentialQuery = pickBy({
    ...omit(
      queryObj,
      [...UTM_TAGS, ...QUERY_BLACKLIST]
    ),
  }, (value) => value != null);
  const escapedQuery = escapeBracesInQuery(essentialQuery);
  const searchString = stringify(escapedQuery);
  const deepLinkPath = pathname ?? location.pathname;
  // NOTE: locale 구조 변경에 따라 앱 하위호환성을 위해 임시적으로 /en-US 등의 값을 /en 으로 바꿔줍니다.
  const path = `${convertLocaleToLanguageInPathname(deepLinkPath)}?${searchString}`;
  const utmData = getUtmData();
  const webviewType = owapWebviewType ?? 'page';
  const owapDeepLink = `ohouseapp://owap?url=https://${host}${path}&webviewType=${webviewType}`;
  const deepLinkHost = isGlobal ? GLOBAL_DEEP_LINK_HOST : DEEP_LINK_HOST;

  const result = `${deepLinkHost}/${utmData['utm_source'] || 'web_organic'}?${stringify({
    deeplink_url: isOwap
      ? owapDeepLink
      : `${isGlobal ? 'ohouseglobalapp' : 'ohouseapp'}://${host}${path}`,
    // fallback_desktop을 공통으로 사용하려면 window.location.href 가 필요하지 않을까?
    fallback_desktop: fallbackDesktopUrl ?? window.location.href,
    campaign: queryObj['campaign'] || utmData['utm_campaign'],
    term: queryObj['term'] || utmData['utm_term'],
    client_id: getPageVariable('visitor_id'),
    sub_id: queryObj['sub_id'] || utmData['bp_sub_id'],
    sub_id_1: 'ohouseWeb',
    sub_id_2: queryObj['sub_id_2'] || utmData['bp_sub_id_2'],
    sub_id_3: queryObj['sub_id_3'] || utmData['utm_medium'],
    ad_creative: queryObj['ad_creative'] || utmData['utm_content'],
    ad_group: queryObj['ad_group'] || utmData['bp_adset'],
    content: queryObj['content'] || utmData['bp_content'],
    ...additionalParams ?? {},
  })}`;
  return result;
}

export const convertLocaleToLanguageInPathname = (pathname: string): string => {
  const splittedPathname = pathname.split('/');
  const localeString = splittedPathname[1] ?? '';

  if (isAllowedGlobalLocale(localeString)) {
    splittedPathname[1] = getGlobalLanguageFromLocaleString(localeString);
    return splittedPathname.join('/');
  }

  return pathname;
};
